import React, { useState } from 'react';

interface CompanyLogoProps {
  ticker: string;
  companyName?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}

const CompanyLogo: React.FC<CompanyLogoProps> = ({ 
  ticker, 
  companyName, 
  size = 'md',
  className = '' 
}) => {
  const [imageError, setImageError] = useState(false);
  
  // Size mappings
  const sizeClasses = {
    sm: 'w-8 h-8 text-xs',
    md: 'w-12 h-12 text-sm',
    lg: 'w-16 h-16 text-base',
    xl: 'w-20 h-20 text-lg'
  };
  
  // Generate initials from company name
  const generateInitials = (name: string): string => {
    if (!name) return ticker.substring(0, 2).toUpperCase();
    
    // Remove common suffixes
    const cleanName = name.replace(/\s*(Inc\.|Inc|Corp\.|Corp|LLC|Ltd\.|Ltd|Company|Co\.|Co)\.?\s*$/i, '').trim();
    
    // Split by spaces and get first letter of each word
    const words = cleanName.split(/\s+/);
    
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    } else if (words.length === 1 && words[0].length >= 2) {
      return words[0].substring(0, 2).toUpperCase();
    }
    
    return words[0][0].toUpperCase();
  };
  
  // Generate consistent color from ticker
  const generateColor = (str: string): string => {
    const colors = [
      'bg-blue-600',
      'bg-purple-600',
      'bg-pink-600',
      'bg-red-600',
      'bg-orange-600',
      'bg-yellow-600',
      'bg-green-600',
      'bg-cyan-600',
      'bg-gray-600',
      'bg-indigo-600'
    ];
    
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    
    const index = Math.abs(hash) % colors.length;
    return colors[index];
  };
  
  const logoUrl = `https://eodhistoricaldata.com/img/logos/US/${ticker.toUpperCase()}.png`;
  const initials = generateInitials(companyName || ticker);
  const bgColor = generateColor(ticker);
  
  if (imageError) {
    // Fallback to initials
    return (
      <div 
        className={`${sizeClasses[size]} ${bgColor} rounded-lg flex items-center justify-center text-white font-semibold ${className}`}
        title={companyName || ticker}
      >
        {initials}
      </div>
    );
  }
  
  return (
    <img
      src={logoUrl}
      alt={`${companyName || ticker} logo`}
      className={`${sizeClasses[size]} rounded-lg object-contain bg-white border border-gray-200 ${className}`}
      onError={() => setImageError(true)}
      loading="lazy"
    />
  );
};

export default CompanyLogo; 